import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal, Nav, Spinner, Table } from "react-bootstrap";
import styled from "styled-components";

import { request } from "hooks/useOkoApi/useOkoApi";

const SearchGroup = styled(Form.Group)`
  margin-top: 16px;
`;

const EntityTable = styled(Table)`
  margin-top: 24px;
`;

const ClickableRow = styled.tr`
  cursor: pointer;
`;

const PaginationBar = styled.div`
  gap: 12px;
  margin-top: 8px;
`;

const PAGE_SIZE = 50;

/**
 * Build the distinct owner dropdown options from the credential list: one option
 * per distinct (client_id XOR school_id) owner that has an Ednition credential.
 * The owner key mirrors the credential's key exactly.
 */
export const distinctOwners = (credentials) => {
  const seen = new Map();
  (credentials || []).forEach((c) => {
    const kind = c.school_id ? "school" : "client";
    const id = c.school_id || c.client_id;
    if (!id) return;
    const key = `${kind}:${id}`;
    if (!seen.has(key)) seen.set(key, { kind, id });
  });
  return Array.from(seen.values());
};

const ownerParams = (owner, secondarySchoolId) => {
  if (!owner) return "";
  if (owner.kind === "school") return `school_id=${owner.id}`;
  const base = `client_id=${owner.id}`;
  return secondarySchoolId ? `${base}&school_id=${secondarySchoolId}` : base;
};

/**
 * Tab 3 — View Entities. Pick an owner (dropdown from the credential list); a
 * client owner exposes a SECONDARY in-client school dropdown. The owner scope is
 * then shown across three sibling tables — Students, Classrooms, Assessments —
 * each on its own sub-tab with independent pagination. Clicking a student row
 * opens a modal with that student's per-assessment detail.
 */
export const ViewEntitiesTab = () => {
  const [owners, setOwners] = useState([]);
  const [nameById, setNameById] = useState({});
  const [selectedKey, setSelectedKey] = useState("");
  const [secondarySchoolId, setSecondarySchoolId] = useState("");
  const [entity, setEntity] = useState("students");
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [creds, schoolData, clientData] = await Promise.all([
          request("integrations/credentials", { method: "GET" }),
          request("admin/schools", { method: "GET" }),
          request("admin/clients", { method: "GET" }),
        ]);
        setOwners(distinctOwners(creds?.credentials));
        const names = {};
        (schoolData?.schools || []).forEach((s) => (names[s.id] = s.name));
        (clientData?.clients || []).forEach((c) => (names[c.id] = c.name));
        setNameById(names);
      } catch (e) {
        setError(e?.detail || "Failed to load owners");
      }
    })();
  }, []);

  const ownerLabel = (o) => `${nameById[o.id] || o.id} (${o.kind})`;

  const selectedOwner = useMemo(
    () => owners.find((o) => `${o.kind}:${o.id}` === selectedKey) || null,
    [owners, selectedKey]
  );

  const ownerParamString = ownerParams(selectedOwner, secondarySchoolId);

  return (
    <div>
      {error && <Alert variant="danger">{error}</Alert>}
      <Form.Group controlId="owner">
        <Form.Label>Owner</Form.Label>
        <Form.Control
          as="select"
          value={selectedKey}
          onChange={(e) => {
            setSelectedKey(e.target.value);
            setSecondarySchoolId("");
          }}
        >
          <option value="">Select an owner…</option>
          {owners.map((o) => (
            <option key={`${o.kind}:${o.id}`} value={`${o.kind}:${o.id}`}>
              {ownerLabel(o)}
            </option>
          ))}
        </Form.Control>
      </Form.Group>

      {selectedOwner && selectedOwner.kind === "client" && (
        <Form.Group controlId="secondarySchool">
          <Form.Label>School (within client)</Form.Label>
          <Form.Control
            type="text"
            placeholder="Optional: narrow to a school_id within this client"
            value={secondarySchoolId}
            onChange={(e) => setSecondarySchoolId(e.target.value)}
          />
        </Form.Group>
      )}

      {selectedOwner && (
        <>
          <Nav
            variant="tabs"
            activeKey={entity}
            onSelect={(k) => setEntity(k)}
            style={{ marginTop: "24px" }}
          >
            <Nav.Item>
              <Nav.Link eventKey="students">Students</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="teachers">Teachers</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="classrooms">Classrooms</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="assessments">Assessments</Nav.Link>
            </Nav.Item>
          </Nav>

          {entity === "students" && (
            <StudentsPanel ownerParamString={ownerParamString} />
          )}
          {entity === "teachers" && (
            <TeachersPanel ownerParamString={ownerParamString} />
          )}
          {entity === "classrooms" && (
            <ClassroomsPanel ownerParamString={ownerParamString} />
          )}
          {entity === "assessments" && (
            <AssessmentsPanel ownerParamString={ownerParamString} />
          )}
        </>
      )}
    </div>
  );
};

/**
 * Shared pager UI + state for one entity table. Resets to page 1 whenever the
 * owner scope changes, and exposes prev/next controls scoped to `total`.
 */
const usePagedEntity = (path, ownerParamString, extraParams) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    setOffset(0);
  }, [ownerParamString, extraParams]);

  const key = extraParams || "";
  const load = useCallback(async () => {
    if (!ownerParamString) return;
    const query = new URLSearchParams(ownerParamString);
    query.set("limit", String(PAGE_SIZE));
    query.set("offset", String(offset));
    if (key) {
      new URLSearchParams(key).forEach((v, k) => query.set(k, v));
    }
    try {
      const data = await request(`${path}?${query.toString()}`, { method: "GET" });
      setRows(data?.[Object.keys(data).find((f) => Array.isArray(data[f]))] || []);
      setTotal(data?.page?.total ?? 0);
      setError(null);
    } catch (e) {
      setError(e?.detail || "Failed to load");
    }
  }, [path, ownerParamString, offset, key]);

  useEffect(() => {
    load();
  }, [load]);

  return { rows, total, offset, setOffset, error };
};

const Pager = ({ total, offset, setOffset }) => {
  const rangeStart = total === 0 ? 0 : offset + 1;
  const rangeEnd = Math.min(offset + PAGE_SIZE, total);
  return (
    <PaginationBar className="d-flex align-items-center">
      <Button
        variant="outline-secondary"
        size="sm"
        disabled={offset === 0}
        onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
      >
        « Prev
      </Button>
      <span className="text-muted">
        {total === 0 ? "None" : `Showing ${rangeStart}–${rangeEnd} of ${total}`}
      </span>
      <Button
        variant="outline-secondary"
        size="sm"
        disabled={rangeEnd >= total}
        onClick={() => setOffset(offset + PAGE_SIZE)}
      >
        Next »
      </Button>
    </PaginationBar>
  );
};

const StudentsPanel = ({ ownerParamString }) => {
  const [search, setSearch] = useState("");
  const extra = search.trim() ? `q=${encodeURIComponent(search.trim())}` : "";
  const { rows, total, offset, setOffset, error } = usePagedEntity(
    "integrations/ednition/students",
    ownerParamString,
    extra
  );
  const [selectedStudent, setSelectedStudent] = useState(null);

  return (
    <div>
      {error && <Alert variant="danger" style={{ marginTop: "16px" }}>{error}</Alert>}
      <SearchGroup controlId="studentSearch">
        <Form.Label>Search students</Form.Label>
        <Form.Control
          type="text"
          placeholder="Filter by email, first name, or last name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </SearchGroup>

      <EntityTable striped bordered hover>
        <thead>
          <tr>
            <th>Email</th>
            <th>First name</th>
            <th>Last name</th>
            <th>School</th>
            <th>Assessments</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <ClickableRow key={s.id} onClick={() => setSelectedStudent(s)}>
              <td>{s.email || s.id}</td>
              <td>{s.first_name}</td>
              <td>{s.last_name}</td>
              <td>{s.school_id}</td>
              <td>{s.assessment_count ?? 0}</td>
            </ClickableRow>
          ))}
        </tbody>
      </EntityTable>

      <Pager total={total} offset={offset} setOffset={setOffset} />

      <StudentAssessmentsModal
        student={selectedStudent}
        ownerParamString={ownerParamString}
        onHide={() => setSelectedStudent(null)}
      />
    </div>
  );
};

const ClassroomsPanel = ({ ownerParamString }) => {
  const { rows, total, offset, setOffset, error } = usePagedEntity(
    "integrations/ednition/classrooms",
    ownerParamString
  );
  const [selectedClassroom, setSelectedClassroom] = useState(null);
  return (
    <div>
      {error && <Alert variant="danger" style={{ marginTop: "16px" }}>{error}</Alert>}
      <EntityTable striped bordered hover>
        <thead>
          <tr>
            <th>Name</th>
            <th>School</th>
            <th>Grade</th>
            <th>Subject</th>
            <th>Year</th>
            <th>Active</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <ClickableRow key={c.id} onClick={() => setSelectedClassroom(c)}>
              <td>{c.name}</td>
              <td>{c.school_id}</td>
              <td>{c.grade_level || "—"}</td>
              <td>{c.subject || "—"}</td>
              <td>{c.academic_year || "—"}</td>
              <td>{c.is_active ? "yes" : "no"}</td>
            </ClickableRow>
          ))}
        </tbody>
      </EntityTable>
      <Pager total={total} offset={offset} setOffset={setOffset} />

      <ClassroomDetailModal
        classroom={selectedClassroom}
        onHide={() => setSelectedClassroom(null)}
      />
    </div>
  );
};

/**
 * Teachers panel. Lists the owner's Ednition-provenance teachers (owner-scoped,
 * paginated via the shared hook). Clicking a teacher row opens a modal listing
 * that teacher's classes — the drill-down endpoint is superuser-only and takes
 * no owner param (the owner is implicit in the resource).
 */
const TeachersPanel = ({ ownerParamString }) => {
  const { rows, total, offset, setOffset, error } = usePagedEntity(
    "integrations/ednition/teachers",
    ownerParamString
  );
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  return (
    <div>
      {error && <Alert variant="danger" style={{ marginTop: "16px" }}>{error}</Alert>}
      <EntityTable striped bordered hover>
        <thead>
          <tr>
            <th>Email</th>
            <th>First name</th>
            <th>Last name</th>
            <th>School</th>
            <th>Classes</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((t) => (
            <ClickableRow key={t.id} onClick={() => setSelectedTeacher(t)}>
              <td>{t.email || t.id}</td>
              <td>{t.first_name}</td>
              <td>{t.last_name}</td>
              <td>{t.school_id}</td>
              <td>{t.classroom_count ?? 0}</td>
            </ClickableRow>
          ))}
        </tbody>
      </EntityTable>

      <Pager total={total} offset={offset} setOffset={setOffset} />

      <TeacherClassroomsModal
        teacher={selectedTeacher}
        onHide={() => setSelectedTeacher(null)}
      />
    </div>
  );
};

const AssessmentsPanel = ({ ownerParamString }) => {
  const { rows, total, offset, setOffset, error } = usePagedEntity(
    "integrations/ednition/assessments",
    ownerParamString
  );
  return (
    <div>
      {error && <Alert variant="danger" style={{ marginTop: "16px" }}>{error}</Alert>}
      <EntityTable striped bordered hover>
        <thead>
          <tr>
            <th>Student</th>
            <th>Source</th>
            <th>Domain / Standard</th>
            <th>Percentile</th>
            <th>Scale score</th>
            <th>OKO band</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              <td>{row.oko_user_id}</td>
              <td>{row.external.source}</td>
              <td>{row.external.domain || row.external.standard || "—"}</td>
              <td>{row.external.percentile ?? "—"}</td>
              <td>{row.external.scale_score ?? "—"}</td>
              <td>
                {row.oko ? `${row.oko.band} (${row.oko.band_label})` : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </EntityTable>
      <Pager total={total} offset={offset} setOffset={setOffset} />
    </div>
  );
};

/**
 * Modal showing a single student's assessment detail. Opens when a
 * student is selected; loads the student's external records and OKO assessments
 * via the reused per-user readers and joins each external record to its
 * OkoAssessment band (1/2/3) and band_label. No assessments -> an empty state,
 * never an error.
 */
export const StudentAssessmentsModal = ({ student, ownerParamString, onHide }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!student) {
      setRows([]);
      return;
    }
    setLoading(true);
    (async () => {
      try {
        const params = `${ownerParamString}&oko_user_id=${student.id}&limit=200`;
        const data = await request(`integrations/ednition/assessments?${params}`, {
          method: "GET",
        });
        // Each roll-up row already pairs the external record with its OKO band.
        setRows(data?.assessments || []);
      } finally {
        setLoading(false);
      }
    })();
  }, [student, ownerParamString]);

  const title = student ? student.email || student.id : "";

  return (
    <Modal show={student !== null} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Assessments — {title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && <Spinner animation="border" size="sm" />}
        {!loading && rows.length === 0 && (
          <p className="text-muted">No assessments for this student.</p>
        )}
        {!loading && rows.length > 0 && (
          <Table striped bordered size="sm">
            <thead>
              <tr>
                <th>Source</th>
                <th>Domain / Standard</th>
                <th>Percentile</th>
                <th>Scale score</th>
                <th>OKO band</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i}>
                  <td>{row.external.source}</td>
                  <td>
                    {row.external.domain || row.external.standard || "—"}
                  </td>
                  <td>{row.external.percentile ?? "—"}</td>
                  <td>{row.external.scale_score ?? "—"}</td>
                  <td>
                    {row.oko
                      ? `${row.oko.band} (${row.oko.band_label})`
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Modal.Body>
    </Modal>
  );
};

/**
 * Modal listing a single teacher's classes. Opens when a teacher row is
 * clicked; GETs the teacher's classrooms via the superuser-only drill-down
 * endpoint (no owner param — the owner is implicit in the resource). No classes
 * -> an empty state, never an error.
 */
export const TeacherClassroomsModal = ({ teacher, onHide }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!teacher) {
      setRows([]);
      return;
    }
    setLoading(true);
    (async () => {
      try {
        const data = await request(
          `integrations/ednition/teachers/${teacher.id}/classrooms?limit=200`,
          { method: "GET" }
        );
        setRows(data?.classrooms || []);
      } finally {
        setLoading(false);
      }
    })();
  }, [teacher]);

  const title = teacher ? teacher.email || teacher.id : "";

  return (
    <Modal show={teacher !== null} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Classes — {title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && <Spinner animation="border" size="sm" />}
        {!loading && rows.length === 0 && (
          <p className="text-muted">No classes for this teacher.</p>
        )}
        {!loading && rows.length > 0 && (
          <Table striped bordered size="sm">
            <thead>
              <tr>
                <th>Name</th>
                <th>Grade</th>
                <th>Subject</th>
                <th>Year</th>
                <th>Active</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id}>
                  <td>{c.name}</td>
                  <td>{c.grade_level || "—"}</td>
                  <td>{c.subject || "—"}</td>
                  <td>{c.academic_year || "—"}</td>
                  <td>{c.is_active ? "yes" : "no"}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Modal.Body>
    </Modal>
  );
};

/**
 * Modal showing a classroom's detail: its teacher(s) and its active students in
 * one view. Opens when a classroom row is clicked; GETs the composite detail
 * endpoint (no owner param — the owner is implicit in the resource). Empty
 * teacher/student sections render an empty state, never an error.
 */
export const ClassroomDetailModal = ({ classroom, onHide }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!classroom) {
      setDetail(null);
      return;
    }
    setLoading(true);
    (async () => {
      try {
        const data = await request(
          `integrations/ednition/classrooms/${classroom.id}/detail`,
          { method: "GET" }
        );
        setDetail(data || null);
      } finally {
        setLoading(false);
      }
    })();
  }, [classroom]);

  const teachers = detail?.teachers || [];
  const students = detail?.students || [];
  const personName = (p) =>
    [p.first_name, p.last_name].filter(Boolean).join(" ") || p.email || p.id;

  return (
    <Modal show={classroom !== null} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Classroom — {classroom ? classroom.name : ""}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && <Spinner animation="border" size="sm" />}
        {!loading && detail && (
          <>
            <h5>Teachers</h5>
            {teachers.length === 0 ? (
              <p className="text-muted">No teachers for this classroom.</p>
            ) : (
              <Table striped bordered size="sm">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Name</th>
                    <th>Primary</th>
                  </tr>
                </thead>
                <tbody>
                  {teachers.map((t) => (
                    <tr key={t.id}>
                      <td>{t.email || t.id}</td>
                      <td>{personName(t)}</td>
                      <td>{t.is_primary ? "★ primary" : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}

            <h5 style={{ marginTop: "16px" }}>Students</h5>
            {students.length === 0 ? (
              <p className="text-muted">No active students for this classroom.</p>
            ) : (
              <Table striped bordered size="sm">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Name</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((s) => (
                    <tr key={s.id}>
                      <td>{s.email || s.id}</td>
                      <td>{personName(s)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};
